import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from "react"
import { useConfig } from "@/config/context"
import { useSettings } from "@/state/SettingsContext"
import { en } from "./messages/en"
import { zh } from "./messages/zh"

const dictionaries: Record<string, Record<string, string>> = { en, zh }

export type Translate = (text: string, vars?: Record<string, string | number>) => string

interface I18nContextValue {
  language: string
  languages: string[]
  setLanguage: (language: string) => void
  t: Translate
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined)

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : match,
  )
}

/**
 * Minimal translation runtime.
 *
 * The English string is the key, exactly like Jumble's `t('Following')` style:
 * a missing translation always renders readable English instead of a raw key.
 */
export function I18nProvider({ children }: { children: ReactNode }) {
  const config = useConfig()
  const { settings, setLanguage } = useSettings()
  const language = dictionaries[settings.language] ? settings.language : config.i18n.defaultLanguage

  useEffect(() => {
    document.documentElement.lang = language
  }, [language])

  const t = useCallback<Translate>(
    (text, vars) => {
      const dictionary = dictionaries[language] ?? {}
      const template = dictionary[text] ?? en[text] ?? text
      return interpolate(template, vars)
    },
    [language],
  )

  const value = useMemo<I18nContextValue>(
    () => ({
      language,
      languages: config.i18n.languages,
      setLanguage,
      t,
    }),
    [language, config.i18n.languages, setLanguage, t],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useTranslation(): I18nContextValue {
  const context = useContext(I18nContext)
  if (!context) throw new Error("useTranslation must be used inside an I18nProvider")
  return context
}
